import React from 'react';
import styled, { css } from 'styled-components';
import { smRegular } from '../theme/typography';
import { themeColor } from '../theme/colors';
import { HudIcon } from '../Icons/HudIcons';
import { ellipsisMixin, transitionFunc } from '../theme/utils';
import Tooltip from '../Tooltip/Tooltip';
import LongContentPopover from '../LongContentPopover/LongContentPopover';
import { useResizeObserver } from '@mantine/hooks';
import { useIsVerticalOverflow } from '../hooks';

export interface TableHeaderProps
  extends React.HTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | false;

  narrow?: boolean;
  width?: number;

  resizable?: boolean;
  isResizing?: boolean;
  resizeHandler?: (event: unknown) => void;
  onAutoFitClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  description?: string;
}

export function TableHeader({
  children,
  sortable,
  sortDirection,
  resizable,
  isResizing,
  onClick,
  onAutoFitClick,
  width,
  narrow,
  resizeHandler,
  description,
  ...props
}: TableHeaderProps) {
  const [ref] = useResizeObserver();
  const isOverflown = useIsVerticalOverflow(ref);
  return (
    <StyledTableHeader
      style={{ width: width ? `${width}px` : undefined }}
      {...props}
    >
      {description ? (
        <Tooltip
          description={
            isOverflown ? children + '\n' + description : description
          }
        >
          <Container $narrow={narrow}>
            {sortable ? (
              <Title $clickable onClick={onClick} role="button">
                <Label ref={ref}>{children}</Label>
                <SortingIndicator>
                  <SortIcon
                    $sorted={sortDirection === 'asc'}
                    icon="other.arrow"
                    $inverted
                    size="6px"
                  />
                  <SortIcon
                    $sorted={sortDirection === 'desc'}
                    icon="other.arrow"
                    size="6px"
                  />
                </SortingIndicator>
              </Title>
            ) : (
              <Title>
                {children}
              </Title>
            )}
            {resizable && (
              <ResizeIndicator
                onMouseDown={resizeHandler} //for desktop
                onTouchStart={resizeHandler} //for mobile
                onDoubleClick={onAutoFitClick}
                $isResizing={isResizing}
              />
            )}
          </Container>
        </Tooltip>
      ) : (
        <Container>
          {sortable ? (
            <Title $clickable $ellipsis={!narrow} onClick={onClick} role="button">
              <Label>
                <LongContentPopover>{children}</LongContentPopover>
              </Label>
              <SortingIndicator>
                <SortIcon
                  $sorted={sortDirection === 'asc'}
                  icon="other.arrow"
                  $inverted
                  size="6px"
                />
                <SortIcon
                  $sorted={sortDirection === 'desc'}
                  icon="other.arrow"
                  size="6px"
                />
              </SortingIndicator>
            </Title>
          ) : (
            <Title>
              <Label>
                <LongContentPopover>{children}</LongContentPopover>
              </Label>
            </Title>
          )}
          {resizable && (
            <ResizeIndicator
              onMouseDown={resizeHandler} //for desktop
              onTouchStart={resizeHandler} //for mobile
              onDoubleClick={onAutoFitClick}
              $isResizing={isResizing}
            />
          )}
        </Container>
      )}
    </StyledTableHeader>
  );
}

export const StyledTableHeader = styled.th`
  ${smRegular}

  user-select: none;
  background: ${themeColor({
    dark: 'tableHeaderBackground',
    light: 'backgroundDark',
  })};
  color: ${themeColor('textPrimary')};
  border-collapse: collapse;
  text-align: start;
  padding: 0;
`;

export const Title = styled.div<{ $clickable?: boolean, $ellipsis?: boolean }>`
  ${({ $ellipsis }) => $ellipsis && ellipsisMixin}
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  word-break: break-all;

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
`;

export const SortingIndicator = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ResizeIndicator = styled.div<{ $isResizing?: boolean }>`
  position: absolute;
  right: 0px;
  z-index: 100;

  width: 4px;
  height: 100%;
  background: ${themeColor('strokeLight')};

  opacity: 0;
  cursor: col-resize;

  transition: opacity 100ms ${transitionFunc};
  ${({ $isResizing }) =>
    $isResizing &&
    css`
      opacity: 1;
      background: ${themeColor('blue')};
    `}
`;

export const SortIcon = styled(HudIcon)<{
  $inverted?: boolean;
  $sorted: boolean;
}>`
  color: ${themeColor('tableHeaderSortAccessor')};

  ${({ $inverted }) =>
    $inverted &&
    css`
      transform: rotate(180deg);
    `}
  ${({ $sorted }) =>
    $sorted &&
    css`
      color: ${themeColor('textSecondary')};
    `}
`;

export const Container = styled.div<{ $narrow?: boolean }>`
  ${smRegular}
  display: flex;
  align-items: center;
  height: 40px;

  position: relative;

  padding-right: 20px;
  ${(props) =>
    props.$narrow
      ? css`
          padding-left: var(--padding-left-narrow);
          padding-right: var(--padding-left-narrow);
        `
      : css`
          padding-left: var(--padding-left-regular);
        `}

  &:hover {
    ${ResizeIndicator} {
      opacity: 1;
    }
  }
`;

export const Label = styled.span`
  ${ellipsisMixin}
`;
